import {
  ActionMenu,
  ConfirmationDialog,
  DataGrid,
  FilterToolbar,
  formatNumber,
  StatusBadge,
  useNotifications,
  type GridQuery
} from '@ijoin/components';
import {
  getGetManagedAccountMethodologyAdvisoryOrganizationsQueryKey,
  getGetManagedAccountMethodologyProvidersQueryKey,
  GetManagedAccountMethodologyProviderRes,
  GetManagedAccountMethodologyProvidersParams,
  useDeleteManagedAccountProviderById,
  useGetManagedAccountMethodologyProviders,
  useGetManagedAccountProviderById
} from '@ijoin/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';

import { camelCase, orderBy } from 'lodash';
import { FC, useState } from 'react';
import { useToggle } from 'usehooks-ts';

import { errorMessage } from '../helpers';
import { LinkButton } from './LinkButton';
import { ManagedAccountProviderFormDialog } from './ManagedAccountProviderFormDialog';

type MethodologyProvidersTabProps = {
  managedAccountMethodologyId: string;
};

export const MethodologyProvidersTab: FC<
  MethodologyProvidersTabProps
> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState<GridQuery>();
  const [editingProviderId, setEditingProviderId] = useState<string>();
  const [pendingDeleteId, setPendingDeleteId] = useState<string>();
  const [isCreateProviderOpen, toggleCreateProviderOpen] = useToggle();

  const providers = useGetManagedAccountMethodologyProviders(
    props.managedAccountMethodologyId,
    query as GetManagedAccountMethodologyProvidersParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );
  const editingProvider = useGetManagedAccountProviderById(
    editingProviderId ?? '',
    { query: { enabled: !!editingProviderId } }
  );

  const deleteProvider = useDeleteManagedAccountProviderById({
    mutation: {
      onError: err =>
        notifications.error(
          errorMessage(err, 'Failed to delete managed account provider.')
        ),
      onSuccess: () => {
        onProviderSaved();
        notifications.success('Managed account provider deleted.');
      }
    }
  });

  const pendingDeleteRow = (providers.data?.data ?? []).find(
    row => row.managedAccountProviderId === pendingDeleteId
  );

  const onCloseProviderEdit = () => setEditingProviderId(undefined);

  const onProviderSaved = () => {
    queryClient.invalidateQueries({
      queryKey: getGetManagedAccountMethodologyProvidersQueryKey(
        props.managedAccountMethodologyId
      )
    });
    queryClient.invalidateQueries({
      queryKey: getGetManagedAccountMethodologyAdvisoryOrganizationsQueryKey(
        props.managedAccountMethodologyId
      )
    });
  };

  const onCancelDelete = () => setPendingDeleteId(undefined);

  const onConfirmDelete = () => {
    if (!pendingDeleteId) return;

    deleteProvider.mutate({ managedAccountProviderId: pendingDeleteId });
    setPendingDeleteId(undefined);
  };

  const columns: GridColDef<GetManagedAccountMethodologyProviderRes>[] = [
    {
      field: 'description',
      flex: 2,
      headerName: 'Name',
      renderCell: params => (
        <LinkButton
          data-testid={camelCase(params.row.description)}
          params={{
            managedAccountProviderId: params.row.managedAccountProviderId
          }}
          to='/managed-account-providers/$managedAccountProviderId'
          variant='text'>
          {params.value}
        </LinkButton>
      )
    },
    {
      display: 'flex',
      field: 'setupComplete',
      flex: 1,
      headerName: 'Setup Complete',
      renderCell: params => (
        <StatusBadge severity={params.value ? 'success' : 'warning'}>
          {params.value ? 'Yes' : 'No'}
        </StatusBadge>
      )
    },
    {
      align: 'right',
      field: 'plansCount',
      headerAlign: 'right',
      headerName: 'Plans',
      sortable: false,
      valueFormatter: value => formatNumber(value),
      width: 100
    },
    {
      align: 'right',
      field: 'portfoliosCount',
      headerAlign: 'right',
      headerName: 'Portfolios',
      sortable: false,
      valueFormatter: value => formatNumber(value),
      width: 120
    },
    {
      align: 'right',
      field: 'advisoryOrganizations',
      headerAlign: 'right',
      headerName: 'Advisory Orgs',
      renderCell: params => (
        <Tooltip
          title={orderBy(params.row.advisoryOrganizations, 'name').map(org => (
            <div key={org.name}>{org.name}</div>
          ))}>
          <span>{formatNumber(params.row.advisoryOrganizations.length)}</span>
        </Tooltip>
      ),
      sortable: false,
      width: 140
    },
    {
      align: 'center',
      display: 'flex',
      field: 'actions',
      headerAlign: 'center',
      headerName: '',
      renderCell: params => (
        <ActionMenu
          actions={[
            {
              /* a disabled MenuItem swallows pointer events, so no Tooltip */
              content: params.row.plansCount
                ? 'Delete (in use by plans)'
                : 'Delete',
              destructive: true,
              disabled: !!params.row.plansCount,
              onSelect: () =>
                setPendingDeleteId(params.row.managedAccountProviderId)
            },
            {
              content: 'Edit',
              onSelect: () =>
                setEditingProviderId(params.row.managedAccountProviderId)
            }
          ]}
          trigger={
            <IconButton
              data-testid={`actions-${camelCase(params.row.description) || params.row.managedAccountProviderId}`}>
              <MoreVertIcon />
            </IconButton>
          }
        />
      ),
      sortable: false,
      width: 65
    }
  ];

  /* these rows are removed by the provider's foreign keys, not by the handler */
  const cascaded = [
    pendingDeleteRow?.portfoliosCount && {
      count: pendingDeleteRow.portfoliosCount,
      label: 'portfolio'
    },
    pendingDeleteRow?.advisoryOrganizations.length && {
      count: pendingDeleteRow.advisoryOrganizations.length,
      label: 'advisory organization link'
    }
  ].filter(entry => !!entry);

  const cascadeWarning = cascaded.length
    ? `This will also delete ${cascaded
        .map(
          entry =>
            `${formatNumber(entry.count)} ${entry.label}${entry.count === 1 ? '' : 's'}`
        )
        .join(' and ')}.`
    : '';

  return (
    <>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={[{ label: 'Name', name: 'description', type: 'text' }]}
        getRowId={row => row.managedAccountProviderId}
        initialSort={[{ field: 'description', sort: 'asc' }]}
        loading={!query || providers.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSize={25}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{ right: ['actions'] }}
        rowCount={providers.data?.pagination?.total ?? 0}
        rows={providers.data?.data ?? []}
        showToolbar
        slotProps={{
          noRowsOverlay: {
            message: 'No managed account providers use this methodology'
          },
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'methodologyManagedAccountProviders'
          },
          toolbar: {
            addLabel: 'Provider',
            addTestId: 'createManagedAccountProvider',
            onAdd: toggleCreateProviderOpen,
            onRefresh: providers.refetch,
            searchButtonTestId: 'search',
            triggerTestId: 'methodologyManagedAccountProviders-FilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />

      {editingProvider.data && (
        <ManagedAccountProviderFormDialog
          isOpen
          managedAccountMethodologyId={props.managedAccountMethodologyId}
          onClose={onCloseProviderEdit}
          onSuccess={onProviderSaved}
          provider={editingProvider.data}
        />
      )}

      {isCreateProviderOpen && (
        <ManagedAccountProviderFormDialog
          isOpen
          managedAccountMethodologyId={props.managedAccountMethodologyId}
          onClose={toggleCreateProviderOpen}
          onSuccess={onProviderSaved}
        />
      )}

      {pendingDeleteRow && (
        <ConfirmationDialog
          confirmationText={pendingDeleteRow.description}
          confirmColor='error'
          confirmText='Delete'
          data-testid='deleteManagedAccountProvider'
          isOpen
          message={
            <Stack spacing={2}>
              <Typography>
                {`Permanently delete "${pendingDeleteRow.description}". This cannot be undone.`}
              </Typography>
              {!!cascadeWarning && (
                <Typography color='error'>{cascadeWarning}</Typography>
              )}
            </Stack>
          }
          onCancel={onCancelDelete}
          onConfirm={onConfirmDelete}
          title='Delete managed account provider'
        />
      )}
    </>
  );
};
